import React, { useState, useEffect, useRef, useCallback } from 'react'

interface CountdownProps {
  /** 倒计时时长（分钟），默认 5 */
  duration?: number
  /** 外层容器 className */
  className?: string
  /** 倒计时结束回调 */
  onExpire?: () => void
  /** 是否隐藏 */
  hidden?: boolean
}

/**
 * 倒计时组件
 * 使用高频 tick + 绝对时间戳计算，避免因屏幕刷新率 / setInterval 漂移导致的"吞秒"问题
 */
export default function Countdown ({ duration = 5, className, onExpire, hidden = false }: CountdownProps) {
  const [seconds, setSeconds] = useState(0)
  const expireAtRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // 记录 onExpire 引用，避免 useEffect 重复执行
  const onExpireRef = useRef<CountdownProps['onExpire']>(onExpire)
  onExpireRef.current = onExpire

  const durationMs = duration * 60 * 1000

  // 重置 / 启动倒计时
  const reset = useCallback(() => {
    expireAtRef.current = Date.now() + durationMs
    setSeconds(Math.ceil(durationMs / 1000))
  }, [durationMs])

  // 首次 + 每次 duration 变化时重置
  useEffect(() => {
    reset()
  }, [reset])

  // 高频 tick：每 100ms 根据绝对时间戳计算剩余秒数
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, expireAtRef.current - Date.now())
      const sec = Math.ceil(remaining / 1000)

      setSeconds(prev => {
        // 仅在秒数真正变化时才更新，减少不必要的渲染
        if (sec !== prev) {
          if (sec === 0) {
            onExpireRef.current && onExpireRef.current()
          }
          return sec
        }
        return prev
      })
    }, 100)

    return () => clearInterval(timerRef.current)
  }, [durationMs])

  const mm = String(Math.floor(seconds / 60)).padStart(2, '0')
  const ss = String(seconds % 60).padStart(2, '0')

  return (
    <span className={className}>
      {mm}:{ss}
    </span>
  )
}
